import { documentsForPlacement } from "../constants/navigationCatalogue";
import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { modulesForDocumentType } from "../constants/documentModule";
import { getModuleRegistryTree, isSubGroup, ModuleEntry, PLANNING_MODULES } from "../constants/modules";
import { useDocumentTypesContext } from "../context/DocumentTypesContext";
import { usePermissions } from "../context/PermissionContext";
import { useTopNavMenu } from "../context/TopNavMenuContext";
import { DocumentType } from "../types/Types";
import { getDocumentPath } from "../routes/documentPath";
import TopNavLink from "./TopNavLink";

// group: entries sharing it sit under one collapsible header inside their section (closed until clicked).
type Entry = { key: string; path: string; label: string; exact?: boolean; state?: Record<string, unknown>; group?: string };
type Section = { key: string; label: string; entries: Entry[] };

const activeStyle = { color: "var(--color-primary)", backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, white)" };

const docState = (doc: DocumentType) => ({
  _id: doc._id,
  code: doc.code,
  name: doc.name,
  category: doc.category,
  subCategory: doc.subCategory,
  subType: doc.subType,
});

const docEntry = (doc: DocumentType): Entry => ({
  key: doc._id,
  path: getDocumentPath(doc),
  label: `${doc.code} ${doc.name}`.trim(),
  state: docState(doc),
});

const qualityControlDocEntries = (
  documentTypes: DocumentType[],
  hasDocNavigate: (docCode: string) => boolean,
  canOpen: (doc: DocumentType) => boolean,
): Entry[] =>
  documentsForPlacement(documentTypes, "qualityControl")
    .sort((a, b) => {
      const numA = parseInt(a.code, 10);
      const numB = parseInt(b.code, 10);
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
      return a.code.localeCompare(b.code);
    })
    .filter((doc) => hasDocNavigate(doc.code) && canOpen(doc))
    .map(docEntry);

const invoiceEntry = (doc: DocumentType): Entry => ({
  key: doc._id,
  path: `/invoice/${doc.code}`,
  label: `${doc.code} ${doc.name}`.trim(),
  state: docState(doc),
});

const CascadingTopMenu: React.FC<{
  menuKey: string;
  label: string;
  sections: Section[];
  panelWidth?: string;
}> = ({ menuKey, label, sections, panelWidth = "w-80" }) => {
  const location = useLocation();
  const [activeSectionKey, setActiveSectionKey] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (group: string) => setOpenGroups((prev) => {
    const next = new Set(prev);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    return next;
  });
  const { activeMenu, openMenu, closeMenu, closeMenuDelayed, cancelClose } = useTopNavMenu();
  const open = activeMenu === menuKey;
  const activeSection = sections.find((section) => section.key === (activeSectionKey || sections[0]?.key)) || sections[0];

  const isEntryActive = (entry: Entry) => (entry.exact
    ? location.pathname === entry.path
    : location.pathname === entry.path || location.pathname.startsWith(`${entry.path}/`));

  if (sections.length === 0) return null;

  return (
    <div className="relative" onMouseEnter={() => openMenu(menuKey)} onMouseLeave={closeMenuDelayed}>
      <button
        type="button"
        onClick={() => (open ? closeMenu() : openMenu(menuKey))}
        className="flex h-9 items-center gap-2 rounded-md px-3 text-sm font-semibold text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/40"
      >
        <span>{label}</span>
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && activeSection && (
        <div
          className="absolute left-0 top-full mt-1 flex rounded-lg border border-gray-200 bg-white text-gray-800 shadow-2xl"
          onMouseEnter={cancelClose}
        >
          <div className="w-56 p-2">
            {sections.map((section) => {
              const active = section.key === activeSection.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  onMouseEnter={() => setActiveSectionKey(section.key)}
                  onClick={() => setActiveSectionKey(section.key)}
                  className={`flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm transition-colors ${
                    active ? "font-semibold" : "text-gray-700 hover:bg-gray-50"
                  }`}
                  style={active ? activeStyle : undefined}
                >
                  <span>{section.label}</span>
                  <ChevronRight size={15} />
                </button>
              );
            })}
          </div>

          <div className={`max-h-[70vh] overflow-y-auto border-l border-gray-200 p-2 ${panelWidth}`}>
            <div className="space-y-1">
              {activeSection.entries.map((entry, index) => {
                const active = isEntryActive(entry);
                const link = (
                  <TopNavLink
                    key={`${activeSection.key}-${entry.path}`}
                    to={entry.path}
                    state={entry.state}
                    label={entry.label}
                    moduleKey={entry.key}
                    isActive={active}
                    onClick={closeMenu}
                    className={`block truncate rounded py-1.5 text-xs transition-colors ${entry.group ? "pl-5 pr-2" : "px-2"} ${
                      active ? "font-semibold" : "text-gray-700 hover:bg-gray-50"
                    }`}
                    style={active ? activeStyle : undefined}
                  />
                );
                if (!entry.group) return link;
                const groupOpen = openGroups.has(entry.group)
                  || activeSection.entries.some((other) => other.group === entry.group && isEntryActive(other));
                const isFirstInGroup = activeSection.entries.findIndex((other) => other.group === entry.group) === index;
                return (
                  <React.Fragment key={`${activeSection.key}-${entry.path}`}>
                    {isFirstInGroup && (
                      <button
                        type="button"
                        onClick={() => toggleGroup(entry.group as string)}
                        className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        <span>{entry.group}</span>
                        {groupOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    )}
                    {groupOpen && link}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const TopRegulatoryMenu: React.FC = () => {
  const { t } = useTranslation();
  const { hasAccess } = usePermissions();
  const regulatoryGroup = getModuleRegistryTree().find((g) => g.key === "regulatory");
  const regulatoryEntry = regulatoryGroup ? regulatoryGroup.children.find((entry) => !isSubGroup(entry)) : undefined;
  const path = regulatoryEntry && !isSubGroup(regulatoryEntry) && regulatoryEntry.path ? regulatoryEntry.path : "/regulatory";
  const labelKey = regulatoryEntry && !isSubGroup(regulatoryEntry) ? regulatoryEntry.labelKey : "sidebar.regulatory";

  if (!hasAccess("REGULATORY")) return null;

  return (
    <CascadingTopMenu
      menuKey="regulatory"
      label={t(labelKey)}
      sections={[{ key: "main", label: t(labelKey), entries: [{ key: "regulatory", path, label: t(labelKey) }] }]}
      panelWidth="w-64"
    />
  );
};

export const TopQualityControlMenu: React.FC = () => {
  const { t } = useTranslation();
  const { documentTypes } = useDocumentTypesContext();
  const { hasAccess, hasDocNavigate, hasDocumentAccess } = usePermissions();
  const canOpen = (doc: DocumentType) => modulesForDocumentType(doc).some((m) => hasDocumentAccess(m, doc.code, "read"));
  if (!hasAccess("QUALITY_CONTROL")) return null;
  const entries: Entry[] = [
    { key: "quality-control", path: "/quality-control", label: t("sidebar.qualityControlLedger", "Главна книга") },
    ...qualityControlDocEntries(documentTypes, hasDocNavigate, canOpen),
  ];
  return (
    <CascadingTopMenu
      menuKey="qualityControl"
      label={t("sidebar.qualityControl")}
      sections={[{ key: "main", label: t("sidebar.qualityControl"), entries }]}
      panelWidth="w-80"
    />
  );
};

export const TopPlanningMenu: React.FC = () => {
  const { t } = useTranslation();
  const { documentTypes } = useDocumentTypesContext();
  const { hasAccess, hasDocNavigate, hasDocumentAccess } = usePermissions();
  const canOpen = (doc: DocumentType) => modulesForDocumentType(doc).some((m) => hasDocumentAccess(m, doc.code, "read"));
  const hasAnyPlanningAccess = PLANNING_MODULES.some((module) => hasAccess(module));
  if (!hasAnyPlanningAccess) return null;

  const entries = documentsForPlacement(documentTypes, "planning")
    .filter((doc) => hasDocNavigate(doc.code) && canOpen(doc))
    .map(docEntry);

  if (entries.length === 0) return null;

  return (
    <CascadingTopMenu
      menuKey="planning"
      label={t("sidebar.planning", "Планирање")}
      sections={[{ key: "documents", label: "Документи", entries }]}
    />
  );
};

export const TopInvoiceMenu: React.FC = () => {
  const { t } = useTranslation();
  const { documentTypes } = useDocumentTypesContext();
  const { hasAccess, hasDocumentAccess } = usePermissions();
  const canOpen = (doc: DocumentType) => modulesForDocumentType(doc).some((m) => hasDocumentAccess(m, doc.code, "read"));
  const invoiceDocs = useMemo(
    () =>
      documentsForPlacement(documentTypes, "invoices")
        .sort((a, b) => parseInt(a.code, 10) - parseInt(b.code, 10) || a.code.localeCompare(b.code)),
    [documentTypes]
  );
  if ((!hasAccess("MATERIAL_DOCUMENTS") && !hasAccess("NON_MATERIAL_DOCUMENTS")) || invoiceDocs.length === 0) return null;
  return (
    <CascadingTopMenu
      menuKey="invoices"
      label={t("sidebar.invoices", "Фактури")}
      sections={[{ key: "documents", label: t("sidebar.invoices", "Фактури"), entries: invoiceDocs.filter(canOpen).map(invoiceEntry) }]}
    />
  );
};

export const TopInventoryMenu: React.FC = () => {
  const { t } = useTranslation();
  const { documentTypes } = useDocumentTypesContext();
  const { hasAccess, isAdmin, hasDocNavigate, hasDocumentAccess } = usePermissions();
  const canOpen = (doc: DocumentType) => modulesForDocumentType(doc).some((m) => hasDocumentAccess(m, doc.code, "read"));
  const docs = useMemo(
    () =>
      documentsForPlacement(documentTypes, "inventory")
        .sort((a, b) => parseInt(a.code, 10) - parseInt(b.code, 10) || a.code.localeCompare(b.code)),
    [documentTypes]
  );
  if (!hasAccess("INVENTORY") && !isAdmin) return null;
  const entries = docs.filter((doc) => hasDocNavigate(doc.code) && canOpen(doc)).map(docEntry);
  if (entries.length === 0) return null;
  return <CascadingTopMenu menuKey="inventory" label={t("sidebar.inventory")} sections={[{ key: "documents", label: t("sidebar.inventory"), entries }]} />;
};

export const TopHRMenu: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin, hasAccess } = usePermissions();
  const entries: Entry[] = [
    ...(hasAccess("HR_BASIC_DATA") ? [{ key: "basic", path: "/hr/basic-data", label: t("sidebar.basicData") }] : []),
    ...(hasAccess("HR_DEPARTMENTS") ? [
      { key: "departments", path: "/hr/departments", label: t("sidebar.departments") },
      { key: "workplaces", path: "/hr/workplaces", label: t("workplaces.title", "Workplaces") },
    ] : []),
    ...(hasAccess("HR_BASIC_DATA") || isAdmin ? [
      { key: "contract-types", path: "/hr/contract-types", label: t("sidebar.contractTypes") },
      { key: "education-levels", path: "/hr/education-levels", label: t("sidebar.educationLevels", "Степен на образование") },
      { key: "salary-grades", path: "/hr/salary-grades", label: t("sidebar.salaryGrades", "Платни групи") },
      { key: "holidays", path: "/hr/holidays", label: t("sidebar.holidays", "Календар на празници") },
      { key: "absence-types", path: "/hr/absence-types", label: t("sidebar.absenceTypes", "Типови отсуство") },
      { key: "shifts", path: "/hr/shifts", label: t("sidebar.shifts", "Смени") },
      { key: "contributions", path: "/hr/contributions", label: t("sidebar.contributions") },
      { key: "payroll", path: "/hr/payroll", label: t("sidebar.payroll") },
    ] : []),
    ...(hasAccess("HR_M1M2") || isAdmin ? [{ key: "m1m2", path: "/hr/m1m2", label: t("sidebar.m1m2", "М1/М2 пријава/одјава") }] : []),
    ...(hasAccess("HR_CONTRACTS") || isAdmin ? [{ key: "contracts", path: "/hr/contracts", label: t("sidebar.contracts", "Договори за вработување") }] : []),
    ...(hasAccess("HR_CERTIFICATES") || isAdmin ? [{ key: "certificates", path: "/hr/certificates", label: t("sidebar.certificates", "Потврди (ПП)") }] : []),
    ...(hasAccess("HR_K15") || isAdmin ? [{ key: "k15", path: "/hr/k15", label: t("sidebar.k15", "К-15") }] : []),
    ...(hasAccess("HR_WORKTIME") || isAdmin ? [{ key: "worktime", path: "/hr/worktime", label: t("sidebar.workTime", "Работно време") }] : []),
    ...(hasAccess("HR_STATISTICS") || isAdmin ? [{ key: "statistics", path: "/hr/statistics", label: t("sidebar.statistics", "Статистика") }] : []),
    ...(hasAccess("HR_TRAINING") || isAdmin ? [
      { key: "training", path: "/hr/training", label: t("sidebar.training", "Обуки") },
      { key: "training-types", path: "/hr/training-types", label: t("sidebar.trainingTypes", "Видови обуки") },
    ] : []),
  ];
  if (entries.length === 0) return null;
  return <CascadingTopMenu menuKey="hr" label={t("sidebar.hr")} sections={[{ key: "main", label: t("sidebar.hr"), entries }]} />;
};

const DEVELOPMENT_CODEBOOK_KEYS = new Set(["MACHINES", "PROCESS_DEFINITIONS", "SUBPROCESSES", "PRODUCTION_STATUSES", "PHASE_TYPES"]);

export const TopSupportSectorsMenu: React.FC = () => {
  const { t } = useTranslation();
  const { documentTypes } = useDocumentTypesContext();
  const { hasAccess, hasDocNavigate, hasDocumentAccess, isAdmin } = usePermissions();
  const canOpen = (doc: DocumentType) => modulesForDocumentType(doc).some((m) => hasDocumentAccess(m, doc.code, "read"));

  const moduleTree = getModuleRegistryTree();
  const devGroup = moduleTree.find((g) => g.key === "development");
  const formulationsSubGroup = devGroup
    ? devGroup.children.find((entry) => isSubGroup(entry) && entry.key === "formulations")
    : undefined;
  const formulationEntries: ModuleEntry[] = formulationsSubGroup && isSubGroup(formulationsSubGroup)
    ? (formulationsSubGroup.children.filter((entry) => !!entry.path) as ModuleEntry[])
    : [];

  const allowedDevelopment: Entry[] = formulationEntries
    .filter((entry) => entry.path && hasAccess(entry.key))
    .map((entry) => ({
      key: entry.key,
      path: entry.path as string,
      label: t(entry.labelKey),
      exact: entry.path === "/os" || entry.path === "/os/archive",
    }));
  // Шифрарниците одат на дното под свој наслов. Само приказ — во регистарот (и во дозволите)
  // остануваат во групата Формулации.
  const developmentCodebooks = allowedDevelopment
    .filter((entry) => DEVELOPMENT_CODEBOOK_KEYS.has(entry.key))
    .map((entry) => ({ ...entry, group: t("sidebar.definitions", "Шифрарници") }));
  const developmentEntries: Entry[] = [
    ...allowedDevelopment.filter((entry) => !DEVELOPMENT_CODEBOOK_KEYS.has(entry.key)),
    ...(hasAccess("INGREDIENTS") && hasAccess("PRINT_CONTROL_PANEL")
      ? [{ key: "PRINT_CONTROL_PANEL", path: "/ingridiens-control-panel", label: t("sidebar.printControlPanel", "Контролна табла") }]
      : []),
    ...developmentCodebooks,
  ];

  const qualityManagementEntries: Entry[] = [
    ...(hasAccess("QUALITY_GROUP_TYPE")
      ? [{ key: "group-type", path: "/qa/typeofgroup", label: t("sidebar.documentations", "Тип на група") }]
      : []),
    ...(hasAccess("QUALITY_DOCUMENTATION")
      ? [
          { key: "doc", path: "/qa/qa", label: "Шифрарник на СОП-ови" },
          { key: "control-panel", path: "/qual-doc-control-panel", label: t("sidebar.docControlPanel", "Контролна табла") },
        ]
      : []),
  ];

  const regulatoryGroup = moduleTree.find((g) => g.key === "regulatory");
  const regulatoryEntry = regulatoryGroup ? regulatoryGroup.children.find((entry) => !isSubGroup(entry)) : undefined;
  const regulatoryPath = regulatoryEntry && !isSubGroup(regulatoryEntry) && regulatoryEntry.path ? regulatoryEntry.path : "/regulatory";
  const regulatoryLabelKey = regulatoryEntry && !isSubGroup(regulatoryEntry) ? regulatoryEntry.labelKey : "sidebar.regulatory";

  const hrEntries: Entry[] = [
    ...(hasAccess("HR_BASIC_DATA") ? [{ key: "basic", path: "/hr/basic-data", label: t("sidebar.basicData") }] : []),
    ...(hasAccess("HR_DEPARTMENTS") ? [
      { key: "departments", path: "/hr/departments", label: t("sidebar.departments") },
      { key: "workplaces", path: "/hr/workplaces", label: t("workplaces.title", "Workplaces") },
    ] : []),
    ...(hasAccess("HR_BASIC_DATA") || isAdmin ? [
      { key: "contract-types", path: "/hr/contract-types", label: t("sidebar.contractTypes") },
      { key: "education-levels", path: "/hr/education-levels", label: t("sidebar.educationLevels", "Степен на образование") },
      { key: "salary-grades", path: "/hr/salary-grades", label: t("sidebar.salaryGrades", "Платни групи") },
      { key: "holidays", path: "/hr/holidays", label: t("sidebar.holidays", "Календар на празници") },
      { key: "absence-types", path: "/hr/absence-types", label: t("sidebar.absenceTypes", "Типови отсуство") },
      { key: "shifts", path: "/hr/shifts", label: t("sidebar.shifts", "Смени") },
      { key: "contributions", path: "/hr/contributions", label: t("sidebar.contributions") },
      { key: "payroll", path: "/hr/payroll", label: t("sidebar.payroll") },
    ] : []),
    ...(hasAccess("HR_M1M2") || isAdmin ? [{ key: "m1m2", path: "/hr/m1m2", label: t("sidebar.m1m2", "М1/М2 пријава/одјава") }] : []),
    ...(hasAccess("HR_CONTRACTS") || isAdmin ? [{ key: "contracts", path: "/hr/contracts", label: t("sidebar.contracts", "Договори за вработување") }] : []),
    ...(hasAccess("HR_CERTIFICATES") || isAdmin ? [{ key: "certificates", path: "/hr/certificates", label: t("sidebar.certificates", "Потврди (ПП)") }] : []),
    ...(hasAccess("HR_K15") || isAdmin ? [{ key: "k15", path: "/hr/k15", label: t("sidebar.k15", "К-15") }] : []),
    ...(hasAccess("HR_WORKTIME") || isAdmin ? [{ key: "worktime", path: "/hr/worktime", label: t("sidebar.workTime", "Работно време") }] : []),
    ...(hasAccess("HR_STATISTICS") || isAdmin ? [{ key: "statistics", path: "/hr/statistics", label: t("sidebar.statistics", "Статистика") }] : []),
    ...(hasAccess("HR_TRAINING") || isAdmin ? [
      { key: "training", path: "/hr/training", label: t("sidebar.training", "Обуки") },
      { key: "training-types", path: "/hr/training-types", label: t("sidebar.trainingTypes", "Видови обуки") },
    ] : []),
  ];

  const sections: Section[] = [
    { key: "development", label: "И&Р", entries: developmentEntries },
    { key: "qualityControl", label: "КК", entries: hasAccess("QUALITY_CONTROL") ? [
      { key: "quality-control", path: "/quality-control", label: t("sidebar.qualityControlLedger", "Главна книга") },
      ...qualityControlDocEntries(documentTypes, hasDocNavigate, canOpen),
    ] : [] },
    { key: "qualityManagement", label: "УК", entries: qualityManagementEntries },
    { key: "regulatory", label: t(regulatoryLabelKey, "Регулатива"), entries: hasAccess("REGULATORY") ? [{ key: "regulatory", path: regulatoryPath, label: t(regulatoryLabelKey) }] : [] },
    { key: "hr", label: "ЧР", entries: hrEntries },
  ].filter((section) => section.entries.length > 0);

  return <CascadingTopMenu menuKey="supportSectors" label="Помошни сектори" sections={sections} panelWidth="w-96" />;
};

export const TopFinanceMenu: React.FC = () => {
  const { t } = useTranslation();
  const { documentTypes } = useDocumentTypesContext();
  const { hasAccess, hasDocNavigate, hasDocumentAccess } = usePermissions();
  const canOpen = (doc: DocumentType) => modulesForDocumentType(doc).some((m) => hasDocumentAccess(m, doc.code, "read"));
  const financeDocs = useMemo(
    () =>
      documentsForPlacement(documentTypes, "financial")
        .sort((a, b) => parseInt(a.code, 10) - parseInt(b.code, 10) || a.code.localeCompare(b.code)),
    [documentTypes]
  );
  const sections: Section[] = [
    { key: "documents", label: "Документи", entries: financeDocs.filter((doc) => hasDocNavigate(doc.code) && canOpen(doc)).map(docEntry) },
    { key: "ledger", label: t("sidebar.generalLedger"), entries: [
      ...(hasAccess("FINANCE")
        ? [{ key: "coa", path: "/finance/general-ledger/chart-of-accounts", label: t("sidebar.chartOfAccounts") }]
        : []),
      ...(hasAccess("FINANCE")
        ? [{ key: "journal", path: "/finance/general-ledger/journal-entries", label: t("sidebar.journalEntries") }]
        : []),
      ...(hasAccess("FINANCE")
        ? [{ key: "trial", path: "/finance/general-ledger/trial-balance", label: t("sidebar.trialBalance") }]
        : []),
      ...(hasAccess("FINANCE")
        ? [{ key: "cards", path: "/finance/general-ledger/analytical-cards", label: t("sidebar.analyticalCards") }]
        : []),
    ] },
    { key: "vat", label: t("sidebar.vat"), entries: [
      ...(hasAccess("FINANCE")
        ? [{ key: "input", path: "/finance/vat/input-register", label: t("sidebar.inputInvoiceRegister") }]
        : []),
      ...(hasAccess("FINANCE")
        ? [{ key: "output", path: "/finance/vat/output-register", label: t("sidebar.outputInvoiceRegister") }]
        : []),
      ...(hasAccess("FINANCE")
        ? [{ key: "ddv", path: "/finance/vat/ddv-04", label: t("sidebar.ddv04") }]
        : []),
    ] },
    { key: "payments", label: t("sidebar.payments"), entries: [
      ...(hasAccess("FINANCE")
        ? [{ key: "orders", path: "/finance/payments/payment-orders", label: t("sidebar.paymentOrders") }]
        : []),
      ...(hasAccess("CASH_REGISTER")
        ? [{ key: "cash", path: "/finance/payments/cash-register", label: t("sidebar.cashRegister") }]
        : []),
    ] },
    { key: "bank", label: t("sidebar.bank"), entries: [
      ...(hasAccess("FINANCE")
        ? [{ key: "statements", path: "/finance/bank/statements", label: t("sidebar.bankStatements") }]
        : []),
    ] },
    { key: "receivables", label: t("sidebar.receivablesPayables"), entries: [
      ...(hasAccess("FINANCE")
        ? [{ key: "customers", path: "/finance/receivables/customers", label: t("sidebar.customerBalances") }]
        : []),
      ...(hasAccess("FINANCE")
        ? [{ key: "suppliers", path: "/finance/receivables/suppliers", label: t("sidebar.supplierBalances") }]
        : []),
      ...(hasAccess("FINANCE")
        ? [{ key: "ios", path: "/finance/receivables/ios", label: t("sidebar.openItemsStatement") }]
        : []),
      ...(hasAccess("FINANCE")
        ? [{ key: "aging", path: "/finance/receivables/aging", label: t("sidebar.agingAnalysis") }]
        : []),
    ] },
    { key: "reports", label: t("sidebar.financialReports"), entries: [
      ...(hasAccess("FINANCE")
        ? [{ key: "trial-report", path: "/finance/general-ledger/trial-balance", label: t("sidebar.trialBalance") }]
        : []),
      ...(hasAccess("FINANCE")
        ? [{ key: "balance", path: "/finance/reports/balance-sheet", label: t("sidebar.balanceSheet") }]
        : []),
      ...(hasAccess("FINANCE")
        ? [{ key: "income", path: "/finance/reports/income-statement", label: t("sidebar.incomeStatement") }]
        : []),
    ] },
    { key: "ujp", label: t("sidebar.ujpFilings"), entries: [
      ...(hasAccess("FINANCE")
        ? [{ key: "ujp-ddv", path: "/finance/ujp/ddv-04", label: t("sidebar.ddv04") }]
        : []),
    ] },
  ].filter((section) => section.entries.length > 0);

  if (sections.length === 0) return null;

  return <CascadingTopMenu menuKey="finance" label={t("sidebar.financialManagement")} sections={sections} panelWidth="w-96" />;
};
